import { Prop } from '@Ravnur-Inc/decorators';
import MediaRepository from '@/repositories/media-repository';
import modalService from '@/helpers/modal.service';
import { languagesService } from '@/modules/common-services';
import SelectCaptionLanguageModal, {
  CaptionSelectableLanguage,
  SelectCaptionLanguageModal$Props,
  SelectCaptionLanguageModal$Response,
} from '@/pages/video-editors/captions-editor-tab/select-caption-language-modal/select-caption-language-modal';
import MediaJobsPollingMixin from '@/pages/video-editors/mixins/media-jobs-polling.mixin';
import { MyMedia, Timedata, TimedataType } from '@Ravnur-Inc/shared/types/Media';
import { Job$Status } from '@/types/Job';
import { showErrorNotification } from '@Ravnur-Inc/notifications/service';
import useSecurityStore from '@/store/security';
import { setup } from 'vue-class-component';
import FileUploader from '@Ravnur-Inc/shared/components/file-uploader/file-uploader';
import { BASE_API_PREFIX } from '@/constants';
import pathes from '@Ravnur-Inc/shared/config/pathes';
import { TimedataAbbreviation } from '@Ravnur-Inc/hooks/video-editors/useTimedataEditor';
import { useCaptionsEditor } from '@Ravnur-Inc/hooks/video-editors/useCaptionsEditor';

import './media-ai-description.scss';

const CN = 'media-ai-description';

const repository = new MediaRepository();

export default class MediaAiDescrption extends MediaJobsPollingMixin {
  declare $refs: {
    [key: string]: FileUploader;
    customCaptionUploader: FileUploader;
  };

  @Prop({ type: Object as () => MyMedia, required: true }) media: MyMedia;

  security = setup(() => useSecurityStore());
  ctx = setup(() => useCaptionsEditor(repository));

  loading = false;
  isActiveAiGeneration = false;
  selectedLanguageId = '';

  get isAutoCaptionsAvailable(): boolean {
    return this.security.isAutoCaptionsAvailable;
  }

  get pathForTimedataUpload() {
    return `${BASE_API_PREFIX}/${pathes.MEDIA}/${this.media.id}/${TimedataAbbreviation.CAPTION}/${this.selectedLanguageId}/upload`;
  }

  get languages(): CaptionSelectableLanguage[] {
    return languagesService.list.map((l) => ({
      label: l.localName,
      code: l.abbreviation,
    }));
  }

  render() {
    return (
      <>
        <r-button
          class={[CN, this.$attrs.class]}
          color="primary"
          disabled={this.loading || this.ctx.loading}
          icon={this.loading || this.ctx.loading ? '' : 'ai-generate'}
          loading={this.loading || this.ctx.loading}
          mode="frameless"
          title={
            this.loading || this.ctx.loading
              ? this.$t('media', 'ai-description-tooltip__loading')
              : this.$t('media', 'ai-description__tooltip')
          }
          onClick={this.handleAIdescription}
        >
          <l10n
            group="media"
            tkey="ai-description__btn"
            v-show={!this.loading && !this.ctx.loading}
          />
        </r-button>

        <FileUploader
          ref="customCaptionUploader"
          accept="text/vtt"
          path={this.pathForTimedataUpload}
          style={{ display: 'none' }}
          onChange={this.onUploadTimedataFile}
        />
      </>
    );
  }

  private async handleAIdescription() {
    try {
      this.loading = true;

      const timedata = await repository.getTimedata(this.media.id);
      const hasCC = timedata?.some((t: Timedata) => t.type === TimedataType.CAPTIONS);

      if (hasCC) {
        await this.generateAiDescription();

        return;
      }

      if (this.mediaJob?.state === Job$Status.RUNNING) {
        return this.showActiveCCGenerationModal();
      }

      const confirm = await this.confirmCCGeneration();

      if (!confirm) {
        return;
      }

      if (this.isAutoCaptionsAvailable) {
        await this.generateCC();
      } else {
        await this.uploadCC();
      }
    } catch (error) {
      this.$processException(error);
    } finally {
      this.loading = false;
    }
  }

  async confirmCCGeneration() {
    const text = !this.isAutoCaptionsAvailable
      ? this.$t('media', 'ai-description__no-captions-modal-text-unable-to-generate')
      : this.$t('media', 'ai-description__no-captions-modal-text');

    const confirmText = !this.isAutoCaptionsAvailable
      ? this.$t('media', 'ai-description__no-captions-confirm-btn-unable-to-generate')
      : this.$t('media', 'ai-description__no-captions-confirm-btn');

    return modalService
      .confirm(text, {
        header: this.$t('media', 'ai-description__info-modal-header'),
        confirmText,
      })
      .catch(() => false);
  }

  async generateAiDescription() {
    this.isActiveAiGeneration = true;

    try {
      modalService
        .confirm(this.$t('media', 'ai-description__info-modal-text'), {
          header: this.$t('media', 'ai-description__info-modal-header'),
          closeLabel: '',
          confirmText: this.$t('common', 'action__ok'),
        })
        .catch(() => false);
      const { description } = await repository.generateAiDescription(this.media.id);

      if (description) {
        this.$emit('description-generated', description);
      }
    } catch (error) {
      console.error(error);
      showErrorNotification(this.$t('media', 'ai-description__error-generating-description'));
    } finally {
      this.isActiveAiGeneration = false;
    }
  }

  async generateCC() {
    try {
      const props: SelectCaptionLanguageModal$Props = {
        header: this.$t('media', 'ai-description__select-language-modal-header'),
        languages: this.languages,
        confirmLabel: this.$t('media', 'ai-description__no-captions-confirm-btn'),
        isDropdown: true,
      };

      const selectedLanguage = (await modalService
        .showModal(SelectCaptionLanguageModal, props)
        .catch(() => false)) as SelectCaptionLanguageModal$Response;

      if (!selectedLanguage) {
        return;
      }

      await repository.generateCC(this.media.id, selectedLanguage.code);
      await this.showActiveCCGenerationModal();

      // immediatelly refresh media jobs
      this.pollMediaJobs(this.media);
    } catch (error) {
      this.$processException(error);
    }
  }

  async uploadCC() {
    try {
      const props: SelectCaptionLanguageModal$Props = {
        header: this.$t('video-editors', 'cc-editor__select-language-upload'),
        languages: this.languages,
        isDropdown: true,
      };

      const selectedLanguage = (await modalService
        .showModal(SelectCaptionLanguageModal, props)
        .catch(() => false)) as SelectCaptionLanguageModal$Response;

      if (!selectedLanguage) {
        return;
      }

      this.selectedLanguageId = selectedLanguage.code;
      this.$refs.customCaptionUploader.browse();
    } catch (error) {
      this.$processException(error);
    }
  }

  async onUploadTimedataFile(file: File) {
    await this.ctx.onUploadTimedataFile(file, this.$refs.customCaptionUploader).finally(() => {
      this.ctx.loading = false;
    });
    this.$refs.customCaptionUploader.clear();
    this.generateAiDescription();
  }

  async showActiveCCGenerationModal() {
    return modalService.confirm(this.$t('media', 'ai-description__generate-cc-success'), {
      header: this.$t('media', 'ai-description__info-modal-header'),
      confirmText: this.$t('common', 'action__ok'),
      closeLabel: '',
    });
  }

  async watchUserLeave() {
    this.$router.beforeEach(async (to, from, next) => {
      if (!this.isActiveAiGeneration) {
        return next();
      }

      const confirm = await modalService
        .confirm(this.$t('media', 'ai-description__leave-confirmation-text'), {
          header: this.$t('media', 'ai-description__leave-confirmation-header'),
          closeLabel: this.$t('media', 'ai-description__leave-confirmation-close-btn'),
          confirmText: this.$t('media', 'ai-description__leave-confirmation-confirm-btn'),
        })
        .catch(() => false);

      if (confirm) {
        this.isActiveAiGeneration = false;
        return next();
      }

      return;
    });

    // watch browser close
    window.addEventListener('beforeunload', (e) => {
      if (this.isActiveAiGeneration) {
        e.preventDefault();
        e.returnValue = '';
      }
    });
  }

  mounted() {
    this.pollMediaJobs(this.media);
    this.watchUserLeave();
  }
}
